import { defineStore } from "pinia";
import { ref, computed, toRaw, watch } from "vue";
import { PrefStorage } from "../pref_storage.js";
import { triggerDownload } from "../tools.js";
import * as TuningLog from "../tuning_log.js";
import { createTuningLogDb } from "../tuning_log_db.js";
import { OPS, emptySyncState, recordChange, pendingCount, mergeLogs, craftKey } from "../tuning_log_sync.js";
import * as Cloud from "../tuning_log_cloud.js";
import { createGitHubClient } from "../github_client.js";
import { useSettingsStore } from "./settings.js";

const prefs = new PrefStorage();

// Shared by every store instance, so a log saved by one is there for the next (and replaceable in
// tests).
let db = createTuningLogDb();

export function setTuningLogDb(newDb) {
  db = newDb;
}

// How long after the last change to wait before syncing (so a burst of edits is one commit), and
// how long to wait before retrying after failing to reach GitHub.
let syncDelayMs = 3000;
let retryDelayMs = 60000;
let clientFactory = createGitHubClient;

/**
 * For tests: replace the GitHub client factory, and the sync delays (null disables automatic
 * syncing - call syncNow() instead).
 */
export function setTuningLogCloudForTests(options) {
  if (options.clientFactory) clientFactory = options.clientFactory;
  if ("syncDelayMs" in options) syncDelayMs = options.syncDelayMs;
  if ("retryDelayMs" in options) retryDelayMs = options.retryDelayMs;
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function changeKey(c) {
  return `${c.op}|${c.entryId || ""}|${c.at}`;
}

function prefGet(name) {
  return new Promise((resolve) => prefs.get(name, resolve));
}

function saveImages(log) {
  return Promise.all(
    (log.entries || []).filter((entry) => entry.image).map((entry) => db.putImage(log.logId, entry.id, entry.image)),
  );
}

export const useTuningLogStore = defineStore("tuningLog", () => {
  const currentLog = ref(null);
  // Sync state (outbox of unsynced changes etc.) for currentLog - see tuning_log_sync.js.
  const currentSync = ref(null);
  // Summaries of every log stored on this computer - see tuning_log_db.js listLogs().
  const localLogs = ref([]);
  // Goes up whenever a log is deleted, so every open list of logs (TuningLogPicker) reloads.
  const deletedLogsVersion = ref(0);
  const aiExpertMode = ref(false);
  const aiUseSkills = ref(true);
  const apiKeyBannerDismissed = ref(false);

  const hasLog = computed(() => currentLog.value !== null);
  const entries = computed(() => (currentLog.value ? currentLog.value.entries : []));
  const totalCostUsd = computed(() =>
    entries.value.reduce((sum, entry) => sum + ((entry.ai && entry.ai.costUsd) || 0), 0),
  );
  const pendingChangeCount = computed(() => pendingCount(currentSync.value));

  // --- Cloud (GitHub) sync - see tuning_log_cloud.js ---
  const settingsStore = useSettingsStore();
  const cloudEnabled = computed(
    () => !!(settingsStore.userSettings.githubRepo && settingsStore.userSettings.githubToken),
  );
  const syncing = ref(false);
  const syncError = ref(null); // { message, offline } from the last failed sync, cleared on success
  // { sentBytes, totalBytes } while a sync is uploading, else null
  const syncProgress = ref(null);
  const lastSyncedAt = ref(null);
  // { logId, at } when a sync last brought changes made on another computer into the current log,
  // so the dialog can say so. Cleared by dismissCloudUpdate() or switching logs.
  const cloudUpdate = ref(null);
  // Unsynced changes across every log on this computer, not just the current one.
  const totalPendingCount = computed(() => {
    const others = localLogs.value
      .filter((l) => !currentLog.value || l.logId !== currentLog.value.logId)
      .reduce((sum, l) => sum + (l.pendingCount || 0), 0);
    return others + pendingChangeCount.value;
  });
  // off | syncing | offline | error | pending | synced
  const syncStatus = computed(() => {
    if (!cloudEnabled.value) return "off";
    if (syncing.value) return "syncing";
    if (syncError.value) return syncError.value.offline ? "offline" : "error";
    if (totalPendingCount.value) return "pending";
    return "synced";
  });

  async function refreshLocalLogs() {
    localLogs.value = await db.listLogs();
  }

  function persist() {
    if (!currentLog.value) return Promise.resolve();

    // A plain copy: items the outbox got back from filtering a reactive array are Vue proxies,
    // which IndexedDB can't store.
    const sync = JSON.parse(JSON.stringify(currentSync.value));
    return db
      .putLog(toRaw(currentLog.value), sync)
      .then(refreshLocalLogs)
      .catch((error) => console.error("Could not save the tuning log locally", error));
  }

  function change(op, entryId) {
    recordChange(currentSync.value, { op, entryId });
    scheduleSync();
    return persist();
  }

  let client = null;
  let clientKey = "";

  function getClient() {
    const settings = settingsStore.userSettings;
    if (!settings.githubRepo || !settings.githubToken) return null;

    const key = `${settings.githubRepo}|${settings.githubBranch}|${settings.githubToken}`;
    if (key !== clientKey) {
      client = clientFactory({ repo: settings.githubRepo, branch: settings.githubBranch, token: settings.githubToken });
      clientKey = key;
    }
    return client;
  }

  let syncTimer = null;

  function scheduleSync(delay = syncDelayMs) {
    // syncDelayMs null: automatic syncing is off (tests) - only syncNow() syncs.
    if (!cloudEnabled.value || syncDelayMs === null || delay === null) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncTimer = null;
      syncNow();
    }, delay);
  }

  /**
   * Stores what a sync of one log produced, keeping any changes made to that log while the sync
   * was running: those are merged on top of the synced copy and stay pending for the next sync.
   */
  async function applySyncResult(start, result) {
    const logId = start.log.logId;
    const isCurrent = currentLog.value && currentLog.value.logId === logId;
    const now = isCurrent
      ? { log: clone(toRaw(currentLog.value)), sync: clone(currentSync.value) }
      : await db.getLog(logId);
    if (!now) return; // Deleted from this computer meanwhile.

    const startKeys = new Set(start.sync.pending.map(changeKey));
    const newPending = now.sync.pending.filter((c) => !startKeys.has(changeKey(c)));

    const log = newPending.length ? mergeLogs(start.log, now.log, result.log) : result.log;
    const sync = { ...result.sync, pending: newPending };

    // Store images that arrived from the cloud (new, or re-captured elsewhere); drop ones whose
    // entries the cloud copy deleted.
    const hadImage = new Map(start.log.entries.filter((e) => e.image).map((e) => [e.id, e.image]));
    const finalIds = new Set(log.entries.map((e) => e.id));
    for (const entry of log.entries) {
      if (entry.image && entry.image !== hadImage.get(entry.id)) await db.putImage(logId, entry.id, entry.image);
    }
    for (const id of hadImage.keys()) {
      if (!finalIds.has(id)) await db.deleteImage(logId, id);
    }

    if (currentLog.value && currentLog.value.logId === logId) {
      currentLog.value = log;
      currentSync.value = sync;
      await persist();
    } else {
      await db.putLog(log, sync);
    }
  }

  async function syncOne(activeClient, logId) {
    const isCurrent = currentLog.value && currentLog.value.logId === logId;
    const start = isCurrent
      ? { log: clone(toRaw(currentLog.value)), sync: clone(currentSync.value) }
      : await db.getLog(logId);
    if (!start) return;

    let result;
    try {
      result = await Cloud.syncLog(activeClient, start.log, start.sync, {
        onProgress: (sentBytes, totalBytes) => {
          syncProgress.value = { sentBytes, totalBytes };
        },
      });
    } finally {
      syncProgress.value = null;
    }
    if (result.deletedInCloud) {
      // Deleted on another computer - delete it here too.
      await deleteLocalLog(logId);
      return;
    }
    if (result.uploaded || result.downloaded || result.sync !== start.sync) {
      await applySyncResult(start, result);
    }
    if (result.downloaded && currentLog.value && currentLog.value.logId === logId) {
      cloudUpdate.value = { logId, at: new Date().toISOString() };
    }
  }

  let syncPromise = null;
  let syncAgain = false;

  /**
   * Syncs every log on this computer that has unsynced changes, plus the current log (to pick up
   * changes made elsewhere). Never throws - a failure is reported through syncError/syncStatus and
   * retried later; nothing local is lost either way.
   */
  function syncNow() {
    if (syncPromise) {
      syncAgain = true;
      return syncPromise;
    }

    const activeClient = getClient();
    if (!activeClient) return Promise.resolve();

    clearTimeout(syncTimer);
    syncing.value = true;
    syncPromise = (async () => {
      let failed = false;
      try {
        if (globalThis.navigator && globalThis.navigator.onLine === false) {
          throw Object.assign(new Error("Offline"), { isNetwork: true });
        }

        const logs = await db.listLogs();
        const ids = logs.filter((l) => l.pendingCount > 0).map((l) => l.logId);
        if (currentLog.value && !ids.includes(currentLog.value.logId)) ids.unshift(currentLog.value.logId);

        for (const id of ids) {
          await syncOne(activeClient, id);
        }

        syncError.value = null;
        lastSyncedAt.value = new Date().toISOString();
      } catch (error) {
        failed = true;
        syncError.value = { message: error.message, offline: !!error.isNetwork };
        if (!error.isNetwork) console.error("Tuning log sync failed", error);
      } finally {
        syncing.value = false;
        syncPromise = null;
        await refreshLocalLogs().catch(() => {});

        if (syncAgain) {
          syncAgain = false;
          scheduleSync(0);
        } else if (failed && totalPendingCount.value) {
          scheduleSync(retryDelayMs);
        }
      }
    })();

    return syncPromise;
  }

  /**
   * Every log in the cloud for a craft (by its name), most recently updated first, each marked
   * with whether it's already on this computer. Rejects if GitHub can't be reached.
   */
  async function listCloudLogs(craftName) {
    const activeClient = getClient();
    if (!activeClient) return [];

    const index = await Cloud.fetchIndex(activeClient);
    const localIds = new Set(localLogs.value.map((l) => l.logId));
    return Cloud.indexRowsForCraft(index, craftName).map((row) => ({ ...row, isLocal: localIds.has(row.logId) }));
  }

  /**
   * Every log for a heli (by craft name) - on this computer and in the cloud - newest first:
   * { logs: [{ logId, name, craftName, entryCount, updated, isLocal, inCloud, newerInCloud,
   *   pendingCount, row }], cloudError } where cloudError says why cloud logs couldn't be listed
   * (e.g. offline), in which case only this computer's logs are included. newerInCloud: this
   * computer has the log, but it's been changed on another computer since it was last synced here.
   */
  async function listLogsForCraft(craftName) {
    await refreshLocalLogs();
    const key = craftKey(craftName);
    const byId = new Map();

    for (const local of localLogs.value.filter((l) => l.craftKey === key)) {
      byId.set(local.logId, {
        logId: local.logId,
        name: local.name,
        craftName: local.craftName,
        entryCount: local.entryCount,
        updated: local.updatedAt,
        isLocal: true,
        inCloud: !!local.lastSyncedAt,
        newerInCloud: false,
        pendingCount: local.pendingCount,
        row: null,
        summary: local,
      });
    }

    let cloudError = null;
    if (cloudEnabled.value) {
      try {
        for (const row of await listCloudLogs(craftName)) {
          const existing = byId.get(row.logId);
          if (existing) {
            existing.inCloud = true;
            existing.newerInCloud = Cloud.isNewerInCloud(row, existing.summary);
            existing.row = row;
            if (row.updated > (existing.updated || "")) existing.updated = row.updated;
          } else {
            byId.set(row.logId, {
              logId: row.logId,
              name: row.name,
              craftName: row.craftName,
              entryCount: row.entryCount,
              updated: row.updated,
              isLocal: false,
              inCloud: true,
              newerInCloud: false,
              pendingCount: 0,
              row,
            });
          }
        }
      } catch (error) {
        cloudError = error.isNetwork ? "Can't reach GitHub - showing logs on this computer only." : error.message;
      }
    }

    for (const item of byId.values()) delete item.summary;
    const logs = [...byId.values()].sort((a, b) => String(b.updated || "").localeCompare(String(a.updated || "")));
    return { logs, cloudError };
  }

  /**
   * Makes a log from listLogsForCraft the current log. Resolves false if it couldn't be opened.
   */
  async function openLog(item) {
    if (!item.isLocal) return openCloudLog(item.row);
    if (!(await switchLog(item.logId))) return false;
    // Changed on another computer - bring those changes in before showing it.
    if (item.newerInCloud) await syncNow();
    return true;
  }

  /**
   * Makes a cloud log (a row from listCloudLogs) the current log - from this computer's copy if it
   * has one (then syncing it), otherwise downloading it with its images. Resolves false if it
   * couldn't be found.
   */
  async function openCloudLog(row) {
    if (await switchLog(row.logId)) {
      return true;
    }

    const activeClient = getClient();
    if (!activeClient) return false;

    const downloaded = await Cloud.downloadLog(activeClient, row);
    if (!downloaded) return false;

    await saveImages(downloaded.log);
    await db.putLog(downloaded.log, downloaded.sync);
    setCurrent(downloaded.log, downloaded.sync);
    await refreshLocalLogs();
    return true;
  }

  function setCurrent(log, sync) {
    if (!log || !currentLog.value || currentLog.value.logId !== log.logId) cloudUpdate.value = null;
    currentLog.value = log;
    currentSync.value = sync;
    prefs.set("tuningLogCurrentId", log ? log.logId : null);
  }

  /**
   * Stores a log that's new to this computer - freshly created or imported - with all of it
   * pending sync, and makes it the current log.
   */
  async function adoptLog(log, op) {
    log.entries = log.entries || [];
    log.deletedEntries = log.deletedEntries || [];

    const sync = recordChange(emptySyncState(), { op });
    setCurrent(log, sync);
    await saveImages(log);
    await persist();
    scheduleSync();
    return currentLog.value;
  }

  /**
   * Before tuning logs were kept in IndexedDB, the one current log lived in localStorage under
   * "tuningLog" - move it across (once) so it isn't lost.
   */
  async function migrateLegacyLog() {
    const legacy = await prefGet("tuningLog");
    if (!legacy || !legacy.logId) return;

    if (!(await db.getLog(legacy.logId))) {
      legacy.entries = legacy.entries || [];
      legacy.deletedEntries = legacy.deletedEntries || [];
      await saveImages(legacy);
      await db.putLog(legacy, recordChange(emptySyncState(), { op: OPS.IMPORT_LOG }));
    }

    if (!(await prefGet("tuningLogCurrentId"))) {
      prefs.set("tuningLogCurrentId", legacy.logId);
    }
    prefs.set("tuningLog", null);
  }

  async function loadFromCache() {
    try {
      await migrateLegacyLog();

      const currentId = await prefGet("tuningLogCurrentId");
      const stored = currentId ? await db.getLog(currentId) : null;

      // Unless a log was already created/imported/switched to while this was loading.
      if (stored && !currentLog.value) {
        currentLog.value = stored.log;
        currentSync.value = stored.sync;
      }

      await refreshLocalLogs();
    } catch (error) {
      console.error("Could not load tuning logs", error);
    }
  }

  function createLog(name, craftName, description) {
    adoptLog(TuningLog.create(name, craftName, description), OPS.CREATE_LOG);
    return currentLog.value;
  }

  function updateDescription(description) {
    if (!currentLog.value || (currentLog.value.description || "") === description) return;

    currentLog.value.description = description;
    change(OPS.UPDATE_DESCRIPTION);
  }

  /**
   * Makes a log already stored on this computer the current one. Resolves false if it isn't there.
   */
  async function switchLog(logId) {
    const stored = await db.getLog(logId);
    if (!stored) return false;

    setCurrent(stored.log, stored.sync);
    scheduleSync(0);
    return true;
  }

  /**
   * Stops using the current log. It stays stored on this computer (see localLogs/switchLog).
   */
  function closeLog() {
    setCurrent(null, null);
  }

  /**
   * Removes a log from this computer entirely, unsynced changes and all.
   */
  async function deleteLocalLog(logId) {
    if (currentLog.value && currentLog.value.logId === logId) {
      setCurrent(null, null);
    }
    await db.deleteLog(logId);
    await refreshLocalLogs();
    deletedLogsVersion.value++;
  }

  /**
   * Deletes a log from listLogsForCraft everywhere: from this computer and, if it's been synced,
   * from the cloud (so other computers drop their copies on their next sync). Throws if the cloud
   * copy couldn't be deleted - the local copy is gone by then, and the log is still listed as in
   * the cloud, so deleting can be retried.
   */
  async function deleteLog(item) {
    const stored = item.isLocal ? await db.getLog(item.logId) : null;
    const cloudDir = (stored && stored.sync && stored.sync.cloudDir) || (item.row && item.row.path) || null;

    if (item.isLocal) await deleteLocalLog(item.logId);

    const activeClient = item.inCloud ? getClient() : null;
    if (!activeClient) return;

    try {
      // Let a sync that's already running finish first, so it can't re-upload the log afterwards.
      if (syncPromise) await syncPromise;
      await Cloud.deleteLog(activeClient, item.logId, cloudDir);
    } finally {
      deletedLogsVersion.value++;
    }
  }

  function findEntry(entryId) {
    return entries.value.find((entry) => entry.id === entryId) || null;
  }

  /**
   * options: { image, config, notes, craftName, timestamp }
   */
  function addEntry(options) {
    if (!currentLog.value) return null;

    const entry = TuningLog.addEntry(currentLog.value, options);
    if (entry.image) {
      db.putImage(currentLog.value.logId, entry.id, entry.image).catch((error) =>
        console.error("Could not save the step response image locally", error),
      );
    }
    change(OPS.ADD_ENTRY, entry.id);
    return entry;
  }

  function deleteEntry(entryId) {
    if (!currentLog.value) return;

    const index = currentLog.value.entries.findIndex((entry) => entry.id === entryId);
    if (index === -1) return;

    currentLog.value.entries.splice(index, 1);
    currentLog.value.deletedEntries = currentLog.value.deletedEntries || [];
    currentLog.value.deletedEntries.push({ id: entryId, deletedAt: new Date().toISOString() });

    db.deleteImage(currentLog.value.logId, entryId).catch(() => {});
    change(OPS.DELETE_ENTRY, entryId);
  }

  function updateEntryNotes(entryId, notes) {
    const entry = findEntry(entryId);
    if (!entry) return;

    entry.notes = notes;
    change(OPS.UPDATE_NOTES, entryId);
  }

  /**
   * Replaces an entry's step response image with a fresh capture (e.g. after changing which axes
   * are shown in the main window). imageUpdatedAt lets the sync tell the newer capture apart.
   */
  function updateEntryImage(entryId, image) {
    const entry = findEntry(entryId);
    if (!entry || !image) return;

    entry.image = image;
    entry.imageUpdatedAt = new Date().toISOString();
    db.putImage(currentLog.value.logId, entry.id, image).catch((error) =>
      console.error("Could not save the step response image locally", error),
    );
    change(OPS.UPDATE_IMAGE, entryId);
  }

  /**
   * Attaches (or with slice = null, removes) an isolated-flight slice on an entry - see
   * flight_slice.js. sliceUpdatedAt lets the sync tell the newer attach/remove apart.
   */
  function updateEntrySlice(entryId, slice) {
    const entry = findEntry(entryId);
    if (!entry) return;

    if (slice) {
      entry.slice = slice;
    } else {
      delete entry.slice;
    }
    entry.sliceUpdatedAt = new Date().toISOString();
    change(OPS.UPDATE_SLICE, entryId);
  }

  /**
   * Marks an entry's attached slice as sent to the AI, so it isn't sent again with every
   * follow-up question.
   */
  function markEntrySliceSent(entryId) {
    const entry = findEntry(entryId);
    if (!entry?.slice) return;

    entry.slice.sentAt = new Date().toISOString();
    entry.sliceUpdatedAt = entry.slice.sentAt;
    change(OPS.UPDATE_SLICE, entryId);
  }

  /**
   * result: { model, conversation, costUsd, usage } - costUsd and usage are added to what's already recorded for
   * this entry (a follow-up question adds to the running total, it doesn't replace it).
   */
  function setEntryAiResult(entryId, result) {
    const entry = findEntry(entryId);
    if (!entry) return;

    entry.ai = entry.ai || {};
    entry.ai.model = result.model;
    entry.ai.conversation = result.conversation;
    entry.ai.costUsd = (entry.ai.costUsd || 0) + (result.costUsd || 0);
    if (result.usage) entry.ai.usage = TuningLog.addUsage(entry.ai.usage, result.usage);
    change(OPS.SET_AI, entryId);
  }

  function importFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        let log;
        try {
          log = JSON.parse(e.target.result);
        } catch {
          reject(new Error("This file is not valid JSON."));
          return;
        }

        const error = TuningLog.validationError(log);
        if (error) {
          reject(new Error(error));
          return;
        }

        adoptLog(log, OPS.IMPORT_LOG).then(resolve, reject);
      };

      reader.onerror = () => reject(new Error("Could not read file"));

      reader.readAsText(file);
    });
  }

  function exportToFile() {
    if (!currentLog.value) return;

    const raw = toRaw(currentLog.value);
    const filename = TuningLog.buildFilename(raw);
    triggerDownload(new Blob([JSON.stringify(raw, null, 2)], { type: "application/json" }), filename);
  }

  function setAiExpertMode(value) {
    aiExpertMode.value = value;
    prefs.set("tuningLogAiExpertMode", value);
  }

  function setAiUseSkills(value) {
    aiUseSkills.value = value;
    prefs.set("tuningLogAiUseSkills", value);
  }

  function dismissCloudUpdate() {
    cloudUpdate.value = null;
  }

  function dismissApiKeyBanner() {
    apiKeyBannerDismissed.value = true;
    prefs.set("tuningLogApiKeyBannerDismissed", true);
  }

  // Restore the persisted tuning log and prefs as soon as the store is created, then catch up with
  // the cloud: upload anything done offline last time, and pick up changes made elsewhere.
  const ready = loadFromCache().then(() => scheduleSync(0));

  if (globalThis.addEventListener) {
    globalThis.addEventListener("online", () => scheduleSync(0));
  }

  // New repo/token in Settings - start using it straight away.
  watch(
    () => [
      settingsStore.userSettings.githubRepo,
      settingsStore.userSettings.githubBranch,
      settingsStore.userSettings.githubToken,
    ],
    () => {
      syncError.value = null;
      scheduleSync(0);
    },
  );
  prefs.get("tuningLogAiExpertMode", (value) => {
    aiExpertMode.value = !!value;
  });
  prefs.get("tuningLogAiUseSkills", (value) => {
    aiUseSkills.value = value === undefined || value === null ? true : !!value;
  });
  prefs.get("tuningLogApiKeyBannerDismissed", (value) => {
    apiKeyBannerDismissed.value = !!value;
  });

  return {
    currentLog,
    currentSync,
    localLogs,
    deletedLogsVersion,
    aiExpertMode,
    aiUseSkills,
    apiKeyBannerDismissed,
    hasLog,
    entries,
    totalCostUsd,
    pendingChangeCount,
    totalPendingCount,
    cloudEnabled,
    syncStatus,
    syncProgress,
    syncError,
    lastSyncedAt,
    cloudUpdate,
    ready,
    syncNow,
    listCloudLogs,
    openCloudLog,
    listLogsForCraft,
    openLog,
    createLog,
    switchLog,
    closeLog,
    deleteLocalLog,
    deleteLog,
    addEntry,
    deleteEntry,
    updateDescription,
    updateEntryNotes,
    updateEntryImage,
    updateEntrySlice,
    markEntrySliceSent,
    setEntryAiResult,
    importFromFile,
    exportToFile,
    setAiExpertMode,
    setAiUseSkills,
    dismissCloudUpdate,
    dismissApiKeyBanner,
  };
});
